import { useLocation, useNavigate } from 'react-router-dom';
import { PageRefTile } from 'components/Layout/Tiles/PageRefTile';
import { useAppStore } from 'hooks/state/useAppStore';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { withSuppressedUrlPersistence } from 'hooks/state/UrlPersistence';
import { FilterValue } from 'components/breadcrumb/breadcrumbModel';
import { ExtendedAttributeCode } from 'pages/trading/model';
import { CountryCodeType } from 'models/countries';
import { PageSectionTitle } from 'components/Layout/PageContainer';
import { useTranslatedAttribute } from 'i18n/translateAttributeHook';
import { categoryLink, productLink } from 'pages/entities/entityhelper';

export interface ExploreMoreSectionProps {
  filters: Record<ExtendedAttributeCode, FilterValue[]>;
}

export function ExploreMoreSection({ filters }: ExploreMoreSectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumbs } = useBreadcrumbStore();
  const { setCategories, setCountries, setFulfillmentMethods, setMarketplaces, setSeasons, setGenders, setProducts } =
    useAppStore();

  const accountNumberParam = (() => {
    const accountNumber = new URLSearchParams(location.search).get('accountNumber');
    return accountNumber ? `?accountNumber=${accountNumber}` : '';
  })();

  const lockedProduct = (filters.prdNumber as string[] | undefined)?.[0];
  const lockedCategory = (filters.prdCategory as string[] | undefined)?.[0];
  const lockedCategoryName = useTranslatedAttribute('prdCategory', lockedCategory ?? '');
  const lockedMarketplaces = filters.chnMarketplace as string[] | undefined;
  const lockedCountries = filters.chnCountry as string[] | undefined;
  const lockedFulfillmentMethods = filters.chnFulfillmentMethod as string[] | undefined;
  const lockedSeasons = filters.prdSeason as string[] | undefined;
  const lockedGenders = filters.prdGender as string[] | undefined;

  const applyLockedFilters = () => {
    if (lockedCategory) setCategories([lockedCategory]);
    if (lockedMarketplaces) setMarketplaces(lockedMarketplaces);
    if (lockedCountries) setCountries(lockedCountries as CountryCodeType[]);
    if (lockedFulfillmentMethods) setFulfillmentMethods(lockedFulfillmentMethods);
    if (lockedSeasons) setSeasons(lockedSeasons);
    if (lockedGenders) setGenders(lockedGenders);
  };

  if (lockedProduct) {
    return (
      <>
        <PageSectionTitle
          titleId="general.title.explore_more"
          additionalTitle={<span className="text-2xl ml-[-10px]">...</span>}
        />
        <PageRefTile
          className="xl:col-span-6"
          iconName="Analytics"
          title={{ id: 'profitability.explore.productDetails.title' }}
          description={{ id: 'profitability.explore.productDetails.description' }}
          buttonText={{ id: 'profitability.explore.productDetails.button' }}
          onClick={() => {
            navigate(productLink(lockedProduct, 'profitability'));
            withSuppressedUrlPersistence(() => {
              applyLockedFilters();
              setBreadcrumbs([]);
            });
          }}
          testId="explore-product-details-card"
        />
        <PageRefTile
          className="xl:col-span-6"
          iconName="Analytics"
          title={{ id: 'profitability.explore.articleVisibility.title' }}
          description={{ id: 'profitability.explore.articleVisibility.description' }}
          buttonText={{ id: 'profitability.explore.articleVisibility.button' }}
          onClick={() => {
            navigate(`/articlevisibility${accountNumberParam}`);
            withSuppressedUrlPersistence(() => {
              applyLockedFilters();
              setProducts([lockedProduct]);
              setBreadcrumbs([]);
            });
          }}
          testId="explore-article-visibility-card"
        />
      </>
    );
  }

  if (lockedCategory) {
    return (
      <>
        <PageSectionTitle
          titleId="general.title.explore_more"
          additionalTitle={<span className="text-2xl ml-[-10px]">...</span>}
        />
        <PageRefTile
          className="xl:col-span-6"
          iconName="Analytics"
          title={{ id: 'profitability.explore.categoryDetails.title' }}
          description={{
            id: 'profitability.explore.categoryDetails.description',
            values: { category: lockedCategoryName },
          }}
          buttonText={{ id: 'profitability.explore.categoryDetails.button' }}
          onClick={() => {
            navigate(categoryLink(lockedCategory, 'profitability'));
            withSuppressedUrlPersistence(() => {
              applyLockedFilters();
              setBreadcrumbs([]);
            });
          }}
          testId="explore-category-details-card"
        />
        <PageRefTile
          className="xl:col-span-6"
          iconName="Analytics"
          title={{ id: 'profitability.explore.topSellers.title' }}
          description={{ id: 'profitability.explore.topSellers.description', values: { category: lockedCategoryName } }}
          buttonText={{ id: 'profitability.explore.topSellers.button' }}
          onClick={() => {
            navigate(`/insights/product/reports/top-selling${accountNumberParam}`);
            withSuppressedUrlPersistence(() => {
              applyLockedFilters();
              setBreadcrumbs([]);
            });
          }}
          testId="explore-top-sellers-card"
        />
      </>
    );
  }

  return null;
}
